// 權限模組共用常數與 helper
// 四分類 precedence（由特殊到一般；reverse-orphan 另由 isReverseOrphan 處理，不在此列）：
//   true-orphan   → DB 有但 backend catalog 無（isInCatalog === false），反灰全鎖
//   half-impl     → backend catalog 有但前端 UI 沒接（key 不在 frontendDeclaredKeys）
//   legacy-pending→ 已納管、前端有接，但尚未切軌（useLegacyPermission=true）
//   healthy       → 完全對齊（isInCatalog + 前端有接 + useLegacyPermission=false）
//
// dbKeySet：呼叫方傳入所有 DB resource key 的 Set，供 isReverseOrphan 使用。
export const ACTIONS = ['view', 'create', 'update', 'delete', 'export', 'approve']
export const DEPENDENT_ACTIONS = ['create', 'update', 'delete', 'export', 'approve']

export const OVERRIDE_MODE = {
  INHERIT: 'inherit',
  GRANT: 'grant',
  REVOKE: 'revoke'
}

export const OVERRIDE_MODE_CODE = {
  GRANT: 1,
  REVOKE: 2
}

// 資料範圍（對應後端 PermissionScope enum）
// Self=0 / Department=1 / Subordinate=2 / All=3
export const SCOPE = {
  SELF: 0,
  DEPARTMENT: 1,
  SUBORDINATE: 2,
  ALL: 3
}

export const SCOPE_OPTIONS = [
  { value: 0, labelKey: 'permission_scope_self', icon: 'mdi-account-outline', color: '#475569' },
  { value: 1, labelKey: 'permission_scope_department', icon: 'mdi-domain', color: '#0284c7' },
  { value: 2, labelKey: 'permission_scope_subordinate', icon: 'mdi-account-tree-outline', color: '#7c3aed' },
  { value: 3, labelKey: 'permission_scope_all', icon: 'mdi-earth', color: '#059669' }
]

// 空值 / undefined → 以 "ALL" 當預設（最寬鬆）
export function scopeMeta (scope) {
  const fallback = SCOPE_OPTIONS.find(s => s.value === SCOPE.ALL)
  if (scope === null || scope === undefined) return fallback
  return SCOPE_OPTIONS.find(s => s.value === Number(scope)) || fallback
}

// Resource 三種角色（kind）：
//   menu    = 控制左側選單分類是否顯示
//   page    = 控制單一路由 / 分頁是否可進入
//   feature = 頁面內的子模組（例：重設密碼、特殊折扣等獨立 toggle）
export const RESOURCE_KIND = {
  MENU: 'menu',
  PAGE: 'page',
  FEATURE: 'feature'
}

// UI 徽章樣式對照：顏色刻意與 matrix 其他元素區隔，一眼能分辨層級
export const KIND_BADGE = {
  menu: {
    icon: 'mdi-view-dashboard-outline',
    chipClass: 'bg-violet-100 text-violet-700 border-violet-200',
    dotClass: 'bg-violet-500',
    labelKey: 'permission_kind_menu'
  },
  page: {
    icon: 'mdi-file-document-outline',
    chipClass: 'bg-sky-100 text-sky-700 border-sky-200',
    dotClass: 'bg-sky-500',
    labelKey: 'permission_kind_page'
  },
  feature: {
    icon: 'mdi-puzzle-outline',
    chipClass: 'bg-amber-100 text-amber-700 border-amber-200',
    dotClass: 'bg-amber-500',
    labelKey: 'permission_kind_feature'
  }
}

export function kindOf (resource) {
  if (!resource) return RESOURCE_KIND.PAGE
  return resource.kind || RESOURCE_KIND.PAGE
}

export function kindMeta (resource) {
  return KIND_BADGE[kindOf(resource)] || KIND_BADGE.page
}

// kind → tooltip/chip icon 色（hex），Matrix/HelpPopover/Switcher 共用
const KIND_ICON_COLOR = {
  menu: '#7c3aed',
  page: '#0284c7',
  feature: '#d97706'
}

export function kindIconColor (kind) {
  return KIND_ICON_COLOR[kind] || '#64748b'
}

export function supportedActionsList (resource) {
  if (!resource || !resource.supportedActions) return []
  return resource.supportedActions.split(',').map(s => s.trim()).filter(Boolean)
}

// 示範用子模組（僅 preview mode 注入，不進 DB）：
// 展示「未來在 permission_users 頁面內加『重設他人密碼』這種 feature 層級功能時」
// UI 會怎麼呈現。讓管理者預先理解三段式階層設計。
export const PREVIEW_SAMPLE_RESOURCES = [
  {
    key: '__preview_reset_password',
    displayName: '（示範）重設他人密碼',
    supportedActions: 'view,update',
    supportsScope: false,
    displayOrder: 25,
    category: '權限管理',
    parentKey: 'permission_users',
    kind: 'feature',
    description: '示範：未來若在「使用者權限管理」頁面加入「重設他人密碼」這類敏感子模組，會以此列呈現。實際權限由對應業務 PR 實作。',
    isPreview: true
  },
  {
    key: '__preview_quotation_discount',
    displayName: '（示範）報價單特殊折扣',
    supportedActions: 'view,update,approve',
    supportsScope: false,
    displayOrder: 110,
    category: '銷售報價',
    parentKey: '__preview_quotation_module',
    kind: 'feature',
    description: '示範：報價頁內的子模組。之後銷售 PR 加入報價單後，可新增這類「特殊折扣 / 不走標準流程」的子權限。',
    isPreview: true
  },
  {
    key: '__preview_quotation_module',
    displayName: '（示範）銷售報價（選單）',
    supportedActions: 'view',
    supportsScope: false,
    displayOrder: 100,
    category: '銷售報價',
    parentKey: null,
    kind: 'menu',
    description: '示範：未來銷售 PR 會 seed 此 menu 層，控制 sidebar「銷售報價」分類是否顯示。',
    isPreview: true
  },
  {
    key: '__preview_quotation',
    displayName: '（示範）報價單',
    supportedActions: 'view,create,update,delete,export,approve',
    supportsScope: true,
    displayOrder: 105,
    category: '銷售報價',
    parentKey: '__preview_quotation_module',
    kind: 'page',
    description: '示範：未來銷售 PR 的 /quotation 頁面權限會長這樣，支援資料範圍（自己/部門/全部）。',
    isPreview: true
  }
]

export function displayName (user) {
  if (!user) return ''
  return user.NickName || user.Title || user.UserName || ''
}

export function initialOf (user) {
  const name = displayName(user)
  return name ? name.slice(0, 1) : ''
}

export function supportsAction (resource, action) {
  if (!resource || !resource.supportedActions) return false
  return resource.supportedActions.split(',').map(s => s.trim()).includes(action)
}

function assertFrontendDeclaredKeys (frontendDeclaredKeys) {
  if (!frontendDeclaredKeys || typeof frontendDeclaredKeys.has !== 'function') {
    throw new TypeError('frontendDeclaredKeys must provide has(key)')
  }
}

export function classifyResource (resource, _dbKeySet, frontendDeclaredKeys) {
  assertFrontendDeclaredKeys(frontendDeclaredKeys)
  if (resource.isInCatalog === false) return 'true-orphan'
  if (!frontendDeclaredKeys.has(resource.key)) return 'half-impl'
  if (resource.useLegacyPermission) return 'legacy-pending'
  return 'healthy'
}

// reverse-orphan：前端有宣告但 DB 中尚未 seed（key 在 frontendDeclaredKeys 但不在 dbKeySet）
export function isReverseOrphan (key, dbKeySet, frontendDeclaredKeys) {
  assertFrontendDeclaredKeys(frontendDeclaredKeys)
  return frontendDeclaredKeys.has(key) && !dbKeySet.has(key)
}

// true-orphan resource 需要反灰全鎖（操作 disabled）
export function isLocked (resource, dbKeySet, frontendDeclaredKeys) {
  return classifyResource(resource, dbKeySet, frontendDeclaredKeys) === 'true-orphan'
}

// 將 flat resources list 轉成 tree
// resources 每筆有 key、parentKey（null = root）、displayOrder
// 回傳 flat list with { resource, depth, isLeaf, childKeys }，順序 = parent followed by children
export function flattenResourceTree (resources) {
  if (!Array.isArray(resources) || resources.length === 0) return []

  const byKey = new Map()
  for (const r of resources) byKey.set(r.key, r)

  const childrenMap = new Map() // parentKey -> [child resources]
  const roots = []
  for (const r of resources) {
    const p = r.parentKey
    if (p && byKey.has(p)) {
      if (!childrenMap.has(p)) childrenMap.set(p, [])
      childrenMap.get(p).push(r)
    } else {
      roots.push(r)
    }
  }

  const sortBy = arr => arr.slice().sort((a, b) => {
    const oa = a.displayOrder != null ? a.displayOrder : 0
    const ob = b.displayOrder != null ? b.displayOrder : 0
    if (oa !== ob) return oa - ob
    return String(a.key).localeCompare(String(b.key))
  })

  const flat = []
  // M-5 fix：加 visited set 防護循環 parentKey（例如 DB 壞資料 A↔B 互為 parent）
  const visited = new Set()
  const visit = (node, depth) => {
    if (visited.has(node.key)) return
    visited.add(node.key)
    const children = sortBy(childrenMap.get(node.key) || [])
    flat.push({
      resource: node,
      depth,
      isLeaf: children.length === 0,
      childKeys: children.map(c => c.key)
    })
    for (const c of children) visit(c, depth + 1)
  }
  for (const root of sortBy(roots)) visit(root, 0)

  return flat
}

// 將 resources 依 tree 取得每個 key 的子孫 keys（遞迴）
export function descendantKeys (resources, parentKey) {
  const childrenMap = new Map()
  for (const r of resources) {
    if (r.parentKey) {
      if (!childrenMap.has(r.parentKey)) childrenMap.set(r.parentKey, [])
      childrenMap.get(r.parentKey).push(r.key)
    }
  }
  const result = []
  // M-5 fix：加 visited 防循環
  const visited = new Set()
  const stack = [...(childrenMap.get(parentKey) || [])]
  while (stack.length > 0) {
    const k = stack.pop()
    if (visited.has(k)) continue
    visited.add(k)
    result.push(k)
    const kids = childrenMap.get(k) || []
    for (const child of kids) stack.push(child)
  }
  return result
}
